import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from "jwt-decode";
import { updateUserLanguage } from '../../socket';
const SettingsContainer = styled.div`
    padding: 30px 20px;
    max-width: 800px;
    margin: 0 auto;
`;

const SettingsSection = styled.div`
    background: linear-gradient(145deg, #f9fbff 0%, #ffffff 100%);
    border-radius: 12px;
    padding: 24px 28px;
    margin-bottom: 25px;
    box-shadow: 0 4px 12px rgba(39, 68, 131, 0.053);
    transition: all 0.3s ease;
    
    &:hover {
        box-shadow: 0 6px 16px rgba(39, 68, 131, 0.07);
    }
`;

const SectionTitle = styled.h2`
    color: #192947;
    margin-bottom: 24px;
    font-size: 1.6rem;
    font-weight: 600;
    position: relative;
    padding-bottom: 10px;
    
    &:after {
        content: '';
        position: absolute;
        left: 0;
        bottom: 0;
        width: 60px;
        height: 3px;
        background: linear-gradient(90deg, #4A76FD 0%, #9747FF 100%);
        border-radius: 3px;
    }
`;

const SettingItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    margin-bottom: 20px;
    padding: 6px 0;
    
    @media (max-width: 768px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const Label = styled.label`
    color: #192947;
    margin-right: 20px;
    min-width: 150px;
    font-size: 1.05rem;
    font-weight: 500;
    
    @media (max-width: 768px) {
        margin-bottom: 10px;
        margin-right: 0;
    }
`;

const Select = styled.select`
    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
    color: #192947;
    border: 1px solid #cad5e9;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 1rem;
    min-width: 220px;
    width: auto;
    max-width: 100%;
    transition: all 0.2s ease;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 14px center;
    padding-right: 40px;
    
    @media (max-width: 768px) {
        width: 100%;
    }
    
    &:focus {
        outline: none;
        border-color: #c6d0f1;
        box-shadow: 0 0 0 2px rgba(74, 118, 253, 0.25);
    }
    
    &:hover {
        background: linear-gradient(135deg, #f8faff 0%, #edf2ff 100%);
        border-color: #b8c7e8;
    }
`;

const HelpText = styled.div`
    color: #455776;
    font-size: 0.9rem;
    margin-top: 6px;
    margin-bottom: 14px;
    margin-left: ${props => props.noIndent ? '0' : '170px'};
    font-style: italic;
    
    @media (max-width: 768px) {
        margin-left: 0;
        margin-top: -5px;
    }
`;

const ProfileAvatar = styled.img`
    width: 64px;
    height: 64px;
    border-radius: 8px;
    object-fit: cover;
    border: 2px solid #e0e8f5;
    box-shadow: 0 2px 6px rgba(39, 68, 131, 0.07);
`;

const Value = styled.div`
    color: #192947;
    word-break: break-all;
`;

const Settings = () => {
    const [selectedLanguage, setSelectedLanguage] = useState('en');
    const [languages, setLanguages] = useState([]);
    const authenticatedAxios = useAuthenticatedAxios();
    const { getAccessTokenSilently, user } = useAuth0();

    useEffect(() => {
        const fetchLanguages = async () => {
            try {
                const token = await getAccessTokenSilently();
                const decodedToken = jwtDecode(token);
                const preferredLanguage = decodedToken.preferred_language || 'en';
                setSelectedLanguage(preferredLanguage);

                const response = await authenticatedAxios.get('api/settings/languages');
                if (response.data && response.data.supportedLanguages) {
                    setLanguages(response.data.supportedLanguages);
                }
            } catch (error) {
                console.error('Error fetching languages:', error);
            }
        };

        fetchLanguages();
    }, [authenticatedAxios, getAccessTokenSilently]);

    const handleLanguageChange = async (e) => {
        setSelectedLanguage(e.target.value);
        const response = await authenticatedAxios.post(`api/settings/language`, {
            language: e.target.value
        });
        updateUserLanguage(e.target.value);
        console.log(response);
        
        // Refresh the browser after language change is complete
        window.location.reload();
    };

    return (
        <SettingsContainer>
            <SettingsSection>
                <SectionTitle>Profile</SectionTitle>
                {user && (
                    <>
                        <SettingItem>
                            <Label>Photo</Label>
                            <ProfileAvatar src={user.picture} alt="User Avatar" />
                        </SettingItem>
                        {user.name && (
                            <SettingItem>
                                <Label>Name</Label>
                                <Value>{user.name}</Value>
                            </SettingItem>
                        )}
                        {user.nickname && (
                            <SettingItem>
                                <Label>Nickname</Label>
                                <Value>{user.nickname}</Value>
                            </SettingItem>
                        )}
                        {user.given_name && (
                            <SettingItem>
                                <Label>Given name</Label>
                                <Value>{user.given_name}</Value>
                            </SettingItem>
                        )}
                        {user.family_name && (
                            <SettingItem>
                                <Label>Family name</Label>
                                <Value>{user.family_name}</Value>
                            </SettingItem>
                        )}
                        {user.email && (
                            <SettingItem>
                                <Label>Email</Label>
                                <Value>
                                    {user.email}{typeof user.email_verified === 'boolean' ? ` (${user.email_verified ? 'verified' : 'unverified'})` : ''}
                                </Value>
                            </SettingItem>
                        )}
                        {user.sub && (
                            <SettingItem>
                                <Label>User ID</Label>
                                <Value>{user.sub}</Value>
                            </SettingItem>
                        )}
                        {user.locale && (
                            <SettingItem>
                                <Label>Locale</Label>
                                <Value>{user.locale}</Value>
                            </SettingItem>
                        )}
                        {user.updated_at && (
                            <SettingItem>
                                <Label>Updated</Label>
                                <Value>{new Date(user.updated_at).toLocaleString()}</Value>
                            </SettingItem>
                        )}
                    </>
                )}
                {!user && (
                    <HelpText noIndent>Profile data is not available.</HelpText>
                )}
            </SettingsSection>
            <SettingsSection>
                <SectionTitle>Chat Settings</SectionTitle>
                <SettingItem>
                    <Label>Chat Language</Label>
                    <Select
                        value={selectedLanguage}
                        onChange={handleLanguageChange}
                    >
                        {languages.map((lang) => (
                            <option key={lang.code} value={lang.code}>
                                {lang.name}
                            </option>
                        ))}
                    </Select>
                </SettingItem>
                <HelpText>* Changing language will apply only to future messages.</HelpText>
            </SettingsSection>
        </SettingsContainer>
    );
};

export default Settings;
